import { duracaoLegivel } from './tema.js';

/**
 * Como uma aula se chama, em português — o vocabulário do `kind`.
 *
 * ## Por que este arquivo nasceu
 *
 * Porque a mesma tabela já existia **três vezes** dentro de `apps/aluno`, e as
 * três não concordavam:
 *
 * | onde | `quiz` vira | cobre todos os tipos? |
 * |---|---|---|
 * | `ListaDaSala.tsx` | `Avaliação` | sim |
 * | `FilaEAcervo.tsx` | `Avaliação` | sim |
 * | `App.tsx` (`LinhaDaAula`) | **`Quiz`** | não — só os cinco raros, de propósito |
 *
 * A terceira é uma decisão e não uma cópia: a página do curso omite `vídeo` e
 * `texto` porque etiquetar 90% das linhas transforma a coluna em papel de
 * parede, e o comentário dela diz isso. O que estava errado eram as **duas
 * primeiras**, idênticas palavra por palavra, mais a divergência do `quiz`, que
 * é do tipo que ninguém repara: as duas telas parecem certas separadas.
 *
 * Aqui fica a lista completa, uma vez. `ListaDaSala` passa a lê-la;
 * `FilaEAcervo` ainda tem a cópia dele (é de outra sessão — está declarado no
 * relatório desta entrega), e `App.tsx` continua com a lista curta, que é uma
 * escolha e não uma duplicata.
 *
 * ## Por que `Avaliação`, e não `Quiz`
 *
 * Porque é o que a sala de aula e `/minhas-aulas` já dizem ao aluno, e porque o
 * aluno vê a palavra no cartão **antes** de abrir a aula: `Quiz` descreve o
 * formato, `Avaliação` descreve o que aquilo vale. Duas das três cópias já
 * diziam assim.
 *
 * ## Por que fica em `lib/`, e não em `@vestigo/core`
 *
 * Porque a régua V13 conta bytes na entrada, e o painel tem a lista dele com
 * ícone e explicação por tipo (`CursoConteudo.tsx`), que é outro objeto. Subir
 * isto ao core faria as duas pontas dividirem uma tabela que só uma delas usa
 * como texto puro — e o motivo de `largura-da-capa.ts` estar no core é o
 * oposto: lá as duas pontas precisam **do mesmo número**.
 */

/** Nove tipos, o mesmo `TipoDeAula` de `lib/api.ts`. `embed` é vídeo para quem assiste. */
export const ROTULO_DO_TIPO: Record<string, string> = {
  video: 'Vídeo',
  texto: 'Texto',
  pdf: 'PDF',
  audio: 'Áudio',
  embed: 'Vídeo',
  live: 'Ao vivo',
  quiz: 'Avaliação',
  galeria: 'Galeria',
  flashcards: 'Flashcards',
};

/**
 * O nome do tipo, ou `Aula` quando não se sabe.
 *
 * A reserva não é defensiva à toa: a prévia do Estúdio monta a aula à mão e pode
 * não mandar `kind`. `Aula` é verdadeiro em qualquer caso — e C5 (§15.7) proíbe
 * `undefined` no texto gerado, que é o que um `ROTULO_DO_TIPO[kind]` cru
 * escreveria na tela.
 */
export function rotuloDoTipo(kind: string | null | undefined, simulado?: boolean): string {
  /*
   * ── A ESPÉCIE OCUPA O LUGAR DO TIPO, e não uma etiqueta ao lado ──────────
   *
   * «Simulado» escreve-se onde «Avaliação» se escrevia. É a mesma pergunta —
   * *o que é isto* — e por isso é o mesmo encaixe: a linha da sala tem três
   * (estado, categoria, grandeza), cada um com um trabalho só, e o comentário
   * de `ListaDaSala` cobra que o quarto candidato derrube um dos três para
   * entrar. A espécie não derruba nenhum: ela AFINA a categoria.
   *
   * O que isto compra, além dos bytes: a palavra viaja de graça para todos os
   * sítios que já chamam esta função — a lista da sala, a página do curso, a
   * ficha acima do palco — e nenhum deles precisa de saber o que é um
   * simulado.
   */
  if (simulado && kind === 'quiz') return 'Simulado';
  return (kind && ROTULO_DO_TIPO[kind]) || 'Aula';
}

/**
 * ── A FICHA DA AULA: o tipo e a medida dele, numa linha ──────────────────
 *
 * `Vídeo · 8 min`, `Texto · 2 min de leitura`, `Avaliação`, `Galeria · 6
 * imagens`. É a linha que fica ao lado do módulo, acima ou abaixo do palco,
 * e que responde a única pergunta que se faz antes de começar: **o que é isto
 * e quanto custa de mim**.
 *
 * ## Por que a medida muda com o tipo
 *
 * Porque cada tipo tem uma unidade honesta e só uma. Um vídeo tem duração;
 * um texto tem tempo de leitura; um baralho tem cartões. Escrever «8 min» num
 * texto seria inventar um relógio que ninguém mediu, e escrever «2 páginas»
 * num vídeo seria pior. Quando não há medida — o PDF antes de abrir, a
 * avaliação, a aula ao vivo —, a ficha diz só o tipo, e não inventa um número
 * para preencher a linha.
 */
export function fichaDaAula(aula: {
  kind: string | null | undefined;
  /** A espécie (0431). A ficha acima do palco diz «Simulado» onde dizia «Avaliação». */
  simulado?: boolean;
  body?: string | null;
  durationSeconds?: number | null;
  content?: Record<string, unknown>;
}): string {
  const tipo = rotuloDoTipo(aula.kind, aula.simulado);
  const medida = medidaDaAula(aula);
  return medida ? `${tipo} · ${medida}` : tipo;
}

/**
 * A medida sozinha, sem a palavra do tipo — a coluna da direita da lista da
 * sala (`MA-sala`, 23/09/2026).
 *
 * Exportada porque a linha da lista passou a escrever as duas metades em
 * ENCAIXES diferentes: a medida à direita, sempre; a palavra só quando ela
 * discorda do tipo dominante do módulo. `fichaDaAula` continua juntando as duas
 * para quem quer a frase inteira, e continua sendo a dona da régua.
 */
export function medidaDaAula(aula: {
  kind: string | null | undefined;
  body?: string | null;
  durationSeconds?: number | null;
  content?: Record<string, unknown>;
}): string {
  switch (aula.kind) {
    case 'video':
    case 'embed':
    case 'audio':
      return duracaoLegivel(aula.durationSeconds);
    case 'texto': {
      const min = tempoDeLeitura(aula.body);
      return min > 0 ? `${min} min de leitura` : '';
    }
    case 'galeria': {
      const n = contar(aula.content?.imagens);
      return n > 0 ? `${n} ${n === 1 ? 'imagem' : 'imagens'}` : '';
    }
    case 'flashcards': {
      const n = contar(aula.content?.cartoes);
      return n > 0 ? `${n} ${n === 1 ? 'cartão' : 'cartões'}` : '';
    }
    default:
      return '';
  }
}

function contar(v: unknown): number {
  return Array.isArray(v) ? v.length : 0;
}

/**
 * Minutos de leitura a 200 palavras por minuto — o ritmo de quem lê material
 * técnico em português com atenção, e não o de quem passa os olhos.
 *
 * Arredonda para cima e tem piso de 1: um parágrafo de 40 palavras é «1 min»,
 * nunca «0 min», porque zero lê como «não há nada para ler».
 */
export function tempoDeLeitura(body: string | null | undefined): number {
  if (!body) return 0;
  const palavras = body.trim().split(/\s+/).filter(Boolean).length;
  if (palavras === 0) return 0;
  return Math.max(1, Math.ceil(palavras / 200));
}
